import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ProctorMonitoring = () => {
  const [activeMonitoring, setActiveMonitoring] = useState(true);

  const liveAssessments = [
    {
      id: 1,
      employee: 'Sarah Johnson',
      assessment: 'Safety Orientation',
      progress: 68,
      timeRemaining: 12,
      flags: 2,
      status: 'in-progress'
    },
    {
      id: 2,
      employee: 'Mike Chen',
      assessment: 'Forklift Certification',
      progress: 45,
      timeRemaining: 25,
      flags: 0,
      status: 'in-progress'
    },
    {
      id: 3,
      employee: 'Emily Davis',
      assessment: 'Hazmat Training',
      progress: 92,
      timeRemaining: 4,
      flags: 1,
      status: 'flagged'
    }
  ];

  const integrityFlags = [
    {
      id: 1,
      employee: 'Emily Davis',
      assessment: 'Hazmat Training',
      flagType: 'Face Not Detected',
      timestamp: '10:23 AM',
      severity: 'medium'
    },
    {
      id: 2,
      employee: 'Sarah Johnson',
      assessment: 'Safety Orientation',
      flagType: 'Multiple Faces Detected',
      timestamp: '10:15 AM',
      severity: 'high'
    },
    {
      id: 3,
      employee: 'Sarah Johnson',
      assessment: 'Safety Orientation',
      flagType: 'Tab Switch Detected',
      timestamp: '10:08 AM',
      severity: 'low'
    }
  ];

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'high':
        return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'medium':
        return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'low':
        return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      default:
        return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  return (
    <div className="space-y-6">
      {/* Monitoring Status */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              activeMonitoring ? 'bg-green-500/10' : 'bg-gray-500/10'
            }`}>
              <Icon 
                name={activeMonitoring ? 'Video' : 'VideoOff'} 
                size={24} 
                color={activeMonitoring ? 'var(--color-success)' : 'var(--color-muted-foreground)'}
              />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Proctoring System</h3>
              <p className="text-sm text-muted-foreground">
                {activeMonitoring ? 'Active monitoring in progress' : 'Monitoring disabled'}
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={activeMonitoring}
              onChange={() => setActiveMonitoring(!activeMonitoring)}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-success"></div>
          </label>
        </div>
      </div>
      {/* Live Monitoring Grid */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">Live Assessments</h3>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-green-500/10 text-green-500 rounded-full text-sm font-medium">
                {liveAssessments?.length} Active
              </span>
            </div>
          </div>
        </div>

        <div className="divide-y divide-border">
          {liveAssessments?.map((assessment) => (
            <div key={assessment?.id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center">
                      <Icon name="User" size={32} color="var(--color-muted-foreground)" />
                    </div>
                    {assessment?.flags > 0 && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{assessment?.flags}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">{assessment?.employee}</h4>
                    <p className="text-sm text-muted-foreground mb-2">{assessment?.assessment}</p>
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Icon name="Clock" size={14} />
                        <span>{assessment?.timeRemaining} min remaining</span>
                      </div>
                      <div className="flex items-center gap-1 text-muted-foreground">
                        <Icon name="Activity" size={14} />
                        <span>{assessment?.progress}% complete</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                    <Icon name="Video" size={20} />
                  </button>
                  <button className="p-2 hover:bg-muted rounded-lg transition-colors">
                    <Icon name="Flag" size={20} />
                  </button>
                  <button className="p-2 hover:bg-muted rounded-lg transition-colors text-destructive">
                    <Icon name="StopCircle" size={20} />
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="w-full bg-muted rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${assessment?.progress}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Integrity Flags */}
      <div className="bg-card border border-border rounded-lg">
        <div className="p-6 border-b border-border">
          <h3 className="text-lg font-semibold text-foreground">Integrity Flags</h3>
        </div>

        <div className="divide-y divide-border">
          {integrityFlags?.map((flag) => (
            <div key={flag?.id} className="p-6 flex items-center justify-between hover:bg-muted/50 transition-colors">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} color="var(--color-destructive)" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium text-foreground">{flag?.employee}</span>
                    <span className="text-sm text-muted-foreground">•</span>
                    <span className="text-sm text-muted-foreground">{flag?.assessment}</span>
                  </div>
                  <p className="text-sm text-foreground">{flag?.flagType}</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-muted-foreground">{flag?.timestamp}</span>
                <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getSeverityColor(flag?.severity)}`}>
                  {flag?.severity}
                </span>
                <button className="text-sm text-primary hover:underline">Review</button>
              </div>
            </div>
          ))}
        </div>
      </div>
      {/* Proctoring Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="text-lg font-semibold text-foreground mb-4">Proctoring Settings</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">Webcam Monitoring</div>
                <div className="text-sm text-muted-foreground">Continuous video monitoring</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">Tab Switch Detection</div>
                <div className="text-sm text-muted-foreground">Alert on browser tab changes</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">Face Detection</div>
                <div className="text-sm text-muted-foreground">Verify test taker identity</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">Screen Recording</div>
                <div className="text-sm text-muted-foreground">Record assessment session</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">Copy/Paste Prevention</div>
                <div className="text-sm text-muted-foreground">Block clipboard operations</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" defaultChecked className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-foreground">Auto-Submit on Violations</div>
                <div className="text-sm text-muted-foreground">Submit test after threshold</div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProctorMonitoring;